/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React, {
  MouseEventHandler,
  useCallback,
  useRef
} from 'react';
import {Button} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {CRUDPermissions, Device, EntityStatus} from '@ion/core/src/types/entities';
import {CellMenu} from '@ion/dls/src/components/grid/CellMenu';
import {CellMenuItem} from '@ion/dls/src/components/grid/CellMenuItem';
import {CreateJobIcon} from '@ion/dls/src/icons/CreateJobIcon';
import {GridRefreshIcon} from '@ion/dls/src/icons/GridRefreshIcon';
import {MapEditIcon} from '@ion/dls/src/icons/MapEditIcon';
import {MoveEntityIcon} from '@ion/dls/src/icons/MoveEntityIcon';
import {NotificationTimeIcon} from '@ion/dls/src/icons/NotificationTimeIcon';
import {ScheduleDataSyncIcon} from '@ion/dls/src/icons/ScheduleDataSyncIcon';
import {SwapIcon} from '@ion/dls/src/icons/SwapIcon';
import {dlsZIndex} from '@ion/dls/src/theme/theme';
import useScheduleDataSync from './useScheduleDataSync';

export interface ViewDeviceTopMenuProps extends ViewDeviceTopMenuCreateJobProps,
  ViewDeviceTopMenuScheduleDataSyncProps {
  onRefresh?: MouseEventHandler<HTMLButtonElement>;
  selectedDevice?: Device| Device[];
  onEdit?: Function;
  onSwap?: Function;
  onAudit?: Function;
  onCreateJob?: Function;
  onMoveDevice?: Function;
  includeEditAction?: boolean;
  hasCreateJob?: boolean;
  devicesPermissions?: CRUDPermissions;
}

export interface ViewDeviceTopMenuCreateJobProps {
  hasFilePermissions?: boolean;
};

export interface ViewDeviceTopMenuScheduleDataSyncProps {
  onScheduleDataSync?: (selectedDevice: Device | Device[]) => void;
}

export const topMenuStyles = makeStyles(() => ({
  formIcons : {
    display        : 'flex',
    justifyContent : 'flex-end',
    alignItems     : 'revert'
  },
  refreshButtonRoot : {
    height    : '41px',
    marginTop : '-3px',
    '&:hover' : {
      cursor : 'pointer'
    }
  }
}));

/**
 * Top Menu for selected Device view
 */
export const ViewDeviceTopMenu = ({
  onRefresh,
  onEdit,
  onSwap,
  onAudit,
  onCreateJob,
  onMoveDevice,
  devicesPermissions,
  onScheduleDataSync,
  selectedDevice,
  hasCreateJob,
  includeEditAction = false,
  hasFilePermissions = false
}: ViewDeviceTopMenuProps) => {
  const classes     = topMenuStyles();
  const cellMenuRef = useRef(null);
  const {
    hasAddPermission,
    hasUpdatePermission
  } = devicesPermissions || {};
  const hasCalampSupport = useScheduleDataSync();

  // Closes the action menu
  const closeMenu = function() {
    cellMenuRef.current.closeCellMenu(cellMenuRef.current, true);
  };

  const editDevice = useCallback(() => {
    onEdit(selectedDevice);
    closeMenu();
  }, [selectedDevice]);

  const swapDevice = useCallback(() => {
    onSwap(selectedDevice);
    closeMenu();
  }, [selectedDevice]);

  const auditDevice = useCallback(() => {
    onAudit(selectedDevice);
    closeMenu();
  }, [selectedDevice]);

  const createJob = useCallback(() => {
    onCreateJob?.(selectedDevice);
    closeMenu();
  }, [selectedDevice]);

  const moveDevice = useCallback(() => {
    onMoveDevice?.(selectedDevice);
    closeMenu();
  }, [selectedDevice]);

  const scheduleDataSync = useCallback(() => {
    onScheduleDataSync?.(selectedDevice);
    closeMenu();
  }, [selectedDevice]);

  return (
    <div className={classes.formIcons}>
      <Button onClick={onRefresh}
        className={classes.refreshButtonRoot}>
        <GridRefreshIcon />
      </Button>
      <div>
        <CellMenu ref={cellMenuRef} mobileDrawerZindex={dlsZIndex('columnChooserDrawer')} menuItems={
          <div>
            {includeEditAction && (
              <CellMenuItem label={translate('admin-groups:grid.menu.edit')}
                icon={<MapEditIcon />}
                disabled={!hasUpdatePermission}
                onClick={editDevice} />
            )}
            <CellMenuItem label={translate('admin-devices:menu.createJob')}
              disabled={!hasCreateJob || hasFilePermissions}
              icon={<CreateJobIcon />}
              onClick={createJob} />
            <CellMenuItem label={translate('admin-devices:menu.move')}
              disabled={!hasUpdatePermission}
              icon={<MoveEntityIcon />}
              onClick={moveDevice} />
            <CellMenuItem label={translate('admin-groups:grid.menu.auditLog')}
              icon={<NotificationTimeIcon />}
              onClick={auditDevice} />
            {selectedDevice?.status === EntityStatus.Enabled && (
              <CellMenuItem label={translate('admin-devices:grid.menu.swapDevice')}
                disabled={!hasAddPermission}
                icon={<SwapIcon />}
                onClick={swapDevice} />
            )}
            {hasCalampSupport && (
              <CellMenuItem label={translate('admin-devices:grid.menu.scheduleDataSync')}
                icon={<ScheduleDataSyncIcon />}
                onClick={scheduleDataSync} />
            )}
          </div>
        } />
      </div>
    </div>
  );
};

export default ViewDeviceTopMenu;
